/**
 * First-touch acquisition record for this browser: the UTM campaign, external
 * referring host, and landing path of the visit that first brought someone to
 * DealHawk. It is written once and only ever filled in (never overwritten), so
 * a later organic visit cannot erase the campaign that actually acquired them.
 */

export const FIRST_TOUCH_STORAGE_KEY = "dealhawk_first_touch";

export const FIRST_TOUCH_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type FirstTouchUtmKey = (typeof FIRST_TOUCH_UTM_KEYS)[number];

export type FirstTouch = Partial<Record<FirstTouchUtmKey, string>> & {
  referrerDomain?: string;
  landingPath?: string;
  firstSeenAt?: number;
};

// Hosts that show up as document.referrer but are part of our own product,
// checkout, or sign-in flows rather than the source that acquired the visitor.
const IGNORED_REFERRER_SUFFIXES = [
  "dealhawkpro.io",
  "dealhawk.io",
  "stripe.com",
  "stripe.network",
  "accounts.google.com",
  "discord.com",
  "discordapp.com",
  "appleid.apple.com",
];

const MAX_VALUE_LENGTH = 256;

function normalizeHost(hostname: string): string | undefined {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!normalized) return undefined;
  return normalized.startsWith("www.") ? normalized.slice(4) : normalized;
}

function hostMatches(host: string, suffix: string) {
  return host === suffix || host.endsWith(`.${suffix}`);
}

/** External referring host, or undefined for internal / auth / checkout hops. */
export function externalReferrerDomain(
  referrer: string,
  currentHostname: string,
): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    const host = normalizeHost(url.hostname);
    if (!host) return undefined;
    const current = normalizeHost(currentHostname);
    if (current && host === current) return undefined;
    if (host === "localhost" || host === "127.0.0.1") return undefined;
    if (IGNORED_REFERRER_SUFFIXES.some((suffix) => hostMatches(host, suffix))) return undefined;
    return host.slice(0, 253);
  } catch {
    return undefined;
  }
}

function readUtm(search: string): Partial<Record<FirstTouchUtmKey, string>> {
  const utm: Partial<Record<FirstTouchUtmKey, string>> = {};
  try {
    const params = new URLSearchParams(search);
    for (const key of FIRST_TOUCH_UTM_KEYS) {
      const value = params.get(key)?.trim();
      if (value) utm[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
  } catch {
    /* Malformed query strings carry no campaign. */
  }
  return utm;
}

function hasUtm(touch: FirstTouch) {
  return FIRST_TOUCH_UTM_KEYS.some((key) => !!touch[key]);
}

export function getFirstTouch(): FirstTouch | null {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const value = parsed as Record<string, unknown>;
    const touch: FirstTouch = {};
    for (const key of FIRST_TOUCH_UTM_KEYS) {
      if (typeof value[key] === "string" && value[key]) touch[key] = value[key] as string;
    }
    if (typeof value.referrerDomain === "string" && value.referrerDomain) {
      touch.referrerDomain = value.referrerDomain;
    }
    if (typeof value.landingPath === "string" && value.landingPath) {
      touch.landingPath = value.landingPath;
    }
    if (typeof value.firstSeenAt === "number" && Number.isFinite(value.firstSeenAt)) {
      touch.firstSeenAt = value.firstSeenAt;
    }
    return touch;
  } catch {
    return null;
  }
}

function writeFirstTouch(touch: FirstTouch) {
  try {
    localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    /* Private mode or blocked storage: attribution is best effort. */
  }
}

/**
 * Record the first touch if none exists. When one already exists without a
 * campaign and this visit carries UTMs, adopt this visit's UTMs as a set while
 * keeping the original referrer, landing path, and timestamp.
 */
export function captureFirstTouch(input?: {
  search?: string;
  pathname?: string;
  referrer?: string;
  hostname?: string;
  now?: number;
}): FirstTouch | null {
  try {
    const search = input?.search ?? window.location.search;
    const pathname = input?.pathname ?? window.location.pathname;
    const referrer = input?.referrer ?? document.referrer;
    const hostname = input?.hostname ?? window.location.hostname;
    const now = input?.now ?? Date.now();
    // OAuth and checkout callbacks are mid-flow hops, never a landing.
    if (pathname.startsWith("/auth/")) return getFirstTouch();

    const utm = readUtm(search);
    const existing = getFirstTouch();
    if (existing) {
      if (!hasUtm(existing) && Object.keys(utm).length) {
        const merged = { ...existing, ...utm };
        writeFirstTouch(merged);
        return merged;
      }
      return existing;
    }

    const touch: FirstTouch = { ...utm, firstSeenAt: now };
    const referrerDomain = externalReferrerDomain(referrer, hostname);
    if (referrerDomain) touch.referrerDomain = referrerDomain;
    const path = String(pathname || "/").split(/[?#]/)[0] || "/";
    touch.landingPath = (path.startsWith("/") ? path : `/${path}`).slice(0, 512);
    writeFirstTouch(touch);
    return touch;
  } catch {
    return null;
  }
}
